import { z } from 'zod';

import { hasUnsentFolioMessage } from './unsent-folio-message';

/**
 * A deploy removes the previous build's hashed chunks, so a tab that is still
 * running that build fails the next time it lazy-loads code it has not loaded
 * yet. Reloading fetches the new build at the same URL.
 *
 * Only a page the user is navigating to reloads on its own: the page they
 * left, and any draft it held, is already gone. Sean's panel outlives the
 * navigation, so a message typed there holds the reload back. Anything that
 * loads in place (Sean, the citation viewer) leaves the reload to the user,
 * so they can save first. The marker in session storage limits automatic
 * reloads to one a minute, so a chunk that is missing from the new build too
 * cannot put the tab in a reload loop.
 *
 * Vite also raises `vite:preloadError` for these failures. It is left alone:
 * preventing its default makes the import resolve to `undefined` instead of
 * rejecting, and the rejection is what tells the nearest boundary whether the
 * user was navigating.
 */

const RELOAD_MARKER_KEY = 'contract-analyzer:chunk-reload';
const RELOAD_GUARD_MS = 60_000;

// Chromium, Firefox and Safari word a failed dynamic import differently; the
// last phrase is Vite's, for a stylesheet the chunk depends on.
const CHUNK_LOAD_FAILURE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

/** Tells this document's own reload apart from the one that loaded it. */
const DOCUMENT_ID = String(performance.timeOrigin);

const reloadMarkerSchema = z.object({ at: z.number(), documentId: z.string() });

type ReloadMarker = z.infer<typeof reloadMarkerSchema>;

/**
 * What became of a page chunk failure: a reload under way, pointless offline,
 * held back for an unsent message to Sean, or held back by the loop guard.
 */
export type ChunkReloadOutcome = 'reloading' | 'offline' | 'folio-message' | 'suppressed';

/** True when a chunk or its stylesheet could not be fetched, rather than failing once loaded. */
export function isChunkLoadError(error: Error): boolean {
  return CHUNK_LOAD_FAILURE.test(error.message);
}

/** A routed page's own chunk failed to load: the user was navigating to it. */
export class PageChunkLoadError extends Error {
  override readonly name = 'PageChunkLoadError';

  constructor(cause: Error) {
    super(cause.message, { cause });
  }
}

function readMarker(): ReloadMarker | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = reloadMarkerSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

function writeMarker(): boolean {
  try {
    window.sessionStorage.setItem(
      RELOAD_MARKER_KEY,
      JSON.stringify({ at: Date.now(), documentId: DOCUMENT_ID }),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * Reloads once to pick up the deployed build after the page being navigated
 * to failed to load. Offline, a reload would only reach the browser's error
 * page; with a message typed to Sean it would discard the message; and
 * without session storage there is no loop guard. None of those reloads.
 */
export function reloadForNewBuild(): ChunkReloadOutcome {
  const marker = readMarker();
  if (marker?.documentId === DOCUMENT_ID) {
    return 'reloading';
  }
  if (!window.navigator.onLine) {
    return 'offline';
  }
  if (hasUnsentFolioMessage()) {
    return 'folio-message';
  }
  if (marker !== null && Date.now() - marker.at < RELOAD_GUARD_MS) {
    return 'suppressed';
  }
  if (!writeMarker()) {
    return 'suppressed';
  }
  window.location.reload();
  return 'reloading';
}

/** Reloads because the user asked to; the marker still holds back an automatic reload after it. */
export function reloadApp(): void {
  writeMarker();
  window.location.reload();
}
